/**
 * App Theme Context - Light/Dark/System preference
 *
 * Provides app-wide theme: user can choose Light, Dark, or System (follow OS).
 * Default is System. Effective theme is applied to the document for CSS.
 */

import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'app-theme';

function getSystemTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const saved = localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null;
  if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  return 'system';
}

interface AppThemeContextType {
  /** Effective theme applied to the document (derived from preference + system when preference is 'system') */
  theme: ThemeMode;
  /** User's stored preference: light, dark, or system */
  themePreference: ThemePreference;
  setThemePreference: (pref: ThemePreference) => void;
  /** Toggle effective theme (sets preference to the opposite of current effective theme) */
  toggleTheme: () => void;
  /** Set preference to an explicit theme (convenience) */
  setTheme: (theme: ThemeMode) => void;
}

const AppThemeContext = createContext<AppThemeContextType | undefined>(undefined);

interface AppThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemePreference;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({
  children,
  defaultTheme = 'system',
}) => {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() =>
    typeof window !== 'undefined' ? getStoredPreference() : defaultTheme
  );

  const [systemTheme, setSystemTheme] = useState<ThemeMode>(getSystemTheme);

  // Listen to OS preference when user has chosen "system"
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = () => setSystemTheme(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  const theme: ThemeMode = useMemo(() => {
    if (themePreference === 'system') return systemTheme;
    return themePreference;
  }, [themePreference, systemTheme]);

  // Apply theme to document and persist preference
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) {
      link.href = theme === 'dark'
        ? '/assets/branding/favicon-dark.svg'
        : '/assets/branding/favicon-light.svg';
    }
  }, [theme, themePreference]);

  const setThemePreference = (pref: ThemePreference) => {
    setThemePreferenceState(pref);
  };

  const setTheme = (t: ThemeMode) => {
    setThemePreferenceState(t);
  };

  const toggleTheme = () => {
    setThemePreferenceState(theme === 'dark' ? 'light' : 'dark');
  };

  const value: AppThemeContextType = {
    theme,
    themePreference,
    setThemePreference,
    toggleTheme,
    setTheme,
  };

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- useAppTheme is hook, AppThemeProvider is component
export const useAppTheme = (): AppThemeContextType => {
  const context = useContext(AppThemeContext);
  if (context === undefined) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
};

export default AppThemeProvider;








